// grid-machinery.ts — the Bridge home grid's GENERIC column machinery
// (owner item 55): sheets-style per-column filtering, left-pinning and
// copy, applied over WHATEVER ColDefs the grid carries — a column added by
// other in-flight work participates with zero wiring here.
//
// This is the console TWIN of the Glance lanes (helm-bridge/src/client/
// glance-filter.ts + glance-presets.ts filter/pin model): same
// ColumnFilterState shape, same operator vocabulary, same three-state pins
// contract (absent = shipped defaults / [] = deliberately unpinned /
// [a,b] = the user's pins). Twinned, not shared — the two clients are
// separate bundles (the .hc-pill / .gl-pill precedent). TWO deliberate
// differences: (1) the Glance filter's doesFilterPass is vacuous-true
// because a pure projection layer pre-filters its rows; the console has no
// such layer, so HERE doesFilterPass is REAL and AG Grid's own pipeline
// drops the rows. (2) THIS side carries a MULTI-VALUE lane (declared in
// glance-filter.ts's header too — active-users-column seam, 2026-09-04):
// a column may present one CELL holding several entities (the Active-users
// avatar stack), and its filter must offer each entity's display string —
// a user NAME — as its own checkbox value, never the joined 'Ann, Bob'
// string. The filter MODEL (ColumnFilterState) is unchanged — selected
// values are still display strings — so seat blobs stay twin-compatible;
// only value COLLECTION (flattened) and the pass rule
// (multiDisplayPassesFilter) know about lists. No Glance column is
// multi-valued today, so the Helm side declares rather than mirrors.

import type { ColDef, IFilterComp, IFilterParams } from 'ag-grid-community';
import { mkFcSelect, mkFcToggle } from './form-controls.js';
import { mkIconBtn } from './buttons.js';

// ---------------------------------------------------------------------
// The filter model — the Glance shapes, byte-compatible.
// ---------------------------------------------------------------------

export type FilterOp =
  | 'contains' | 'not-contains' | 'equals' | 'not-equals'
  | 'starts' | 'ends' | 'gt' | 'gte' | 'lt' | 'lte' | 'blank' | 'not-blank';

export interface ColumnFilterState {
  /** Display values kept (null/absent = all). */
  selected?: string[] | null;
  op?: FilterOp | null;
  operand?: string;
}

/** colId → state. Only ACTIVE entries constrain (columnFilterActive). */
export type ColumnFilterMap = Record<string, ColumnFilterState>;

export const BLANK_VALUE = '(Blanks)';

export function columnFilterActive(f: ColumnFilterState | undefined | null): boolean {
  if (!f) return false;
  if (f.selected && f.selected !== null) return true;
  if (f.op === 'blank' || f.op === 'not-blank') return true;
  return !!(f.op && String(f.operand ?? '').trim() !== '');
}

export function anyColumnFilterActive(filters: ColumnFilterMap | undefined | null): boolean {
  if (!filters) return false;
  return Object.values(filters).some(columnFilterActive);
}

/** '$1,234.56' / '87%' / '42' → the number, or null. The console's numeric
 * cells format currency and percentages; criteria comparisons must see the
 * quantity, not the string. */
export function numericOf(display: string): number | null {
  const s = display.trim().replace(/[$,% ]/g, '').replace(/,/g, '');
  if (s === '') return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

function criteriaPass(display: string, f: ColumnFilterState): boolean {
  const op = f.op;
  if (!op) return true;
  if (op === 'blank') return display === '';
  if (op === 'not-blank') return display !== '';
  const operand = String(f.operand ?? '').trim();
  if (operand === '') return true;
  const cmpN = numericOf(operand);
  const rawN = numericOf(display);
  if (cmpN !== null && ['gt', 'gte', 'lt', 'lte', 'equals', 'not-equals'].includes(op)) {
    if (rawN !== null) {
      switch (op) {
        case 'gt': return rawN > cmpN;
        case 'gte': return rawN >= cmpN;
        case 'lt': return rawN < cmpN;
        case 'lte': return rawN <= cmpN;
        case 'equals': return rawN === cmpN;
        case 'not-equals': return rawN !== cmpN;
      }
    }
    // A numeric operand against a non-numeric cell: only the (in)equality
    // ops can still speak honestly, as string comparisons below.
    if (op !== 'equals' && op !== 'not-equals') return false;
  }
  const hay = display.toLowerCase();
  const needle = operand.toLowerCase();
  switch (op) {
    case 'contains': return hay.includes(needle);
    case 'not-contains': return !hay.includes(needle);
    case 'equals': return hay === needle;
    case 'not-equals': return hay !== needle;
    case 'starts': return hay.startsWith(needle);
    case 'ends': return hay.endsWith(needle);
    case 'gt': return hay > needle;
    case 'gte': return hay >= needle;
    case 'lt': return hay < needle;
    case 'lte': return hay <= needle;
    default: return true;
  }
}

/** One cell against one column's filter — display text is the whole
 * vocabulary (what the eye reads is what the filter matches). */
export function displayPassesFilter(display: string, f: ColumnFilterState | undefined | null): boolean {
  if (!f || !columnFilterActive(f)) return true;
  if (f.selected && f.selected !== null) {
    const key = display || BLANK_VALUE;
    if (!f.selected.includes(key)) return false;
  }
  return criteriaPass(display, f);
}

/** One MULTI-VALUED cell against one column's filter (the Active-users
 * lane: a cell that IS a list of entities, filtered by each entity's own
 * display string — user names — never the joined text).
 *
 * Semantics, chosen so the filter answers the question a person asks:
 *   - empty list = a BLANK cell: rides the single-value lane on '' (so
 *     (Blanks) selection, 'blank'/'not-blank' all agree with collection).
 *   - value selection: keep the row when ANY entity is selected — ticking
 *     'Ann' means "releases Ann is in".
 *   - positive criteria (contains/equals/starts/...): ANY entity may
 *     satisfy — 'contains ann' finds every release with an Ann.
 *   - NEGATIVE criteria (not-contains / not-equals): EVERY entity must
 *     satisfy — 'not-equals Ann' means "Ann is NOT in this release", not
 *     "someone other than Ann is also here". some() on a negation would
 *     keep almost every row and read as a broken filter. */
export function multiDisplayPassesFilter(
  displays: string[],
  f: ColumnFilterState | undefined | null,
): boolean {
  if (!f || !columnFilterActive(f)) return true;
  const list = displays.filter((d) => d !== '');
  if (list.length === 0) return displayPassesFilter('', f);
  if (f.selected && f.selected !== null) {
    const sel = f.selected;
    if (!list.some((d) => sel.includes(d))) return false;
  }
  const op = f.op;
  if (!op) return true;
  if (op === 'not-contains' || op === 'not-equals') {
    return list.every((d) => criteriaPass(d, f));
  }
  return list.some((d) => criteriaPass(d, f));
}

/** Distinct display values with counts, sorted, blanks named last. */
export function collectValues(displays: string[]): { value: string; count: number }[] {
  const counts = new Map<string, number>();
  for (const d of displays) {
    const v = d || BLANK_VALUE;
    counts.set(v, (counts.get(v) ?? 0) + 1);
  }
  const out = [...counts.entries()].map(([value, count]) => ({ value, count }));
  out.sort((a, b) => {
    if (a.value === BLANK_VALUE) return 1;
    if (b.value === BLANK_VALUE) return -1;
    return a.value.localeCompare(b.value, undefined, { numeric: true });
  });
  return out;
}

// ---------------------------------------------------------------------
// Pins — the three-state contract (Glance twin).
// ---------------------------------------------------------------------

/** Resolve a seat blob's pins field: undefined/null = the shipped defaults;
 * an array is honored minus colIds the grid no longer carries. */
export function effectivePins(
  pins: string[] | null | undefined,
  defaultPins: readonly string[],
  knownIds: readonly string[],
): string[] {
  const known = new Set(knownIds);
  const base = pins === null || pins === undefined ? [...defaultPins] : pins;
  return base.filter((c) => known.has(c));
}

/** Toggle one column in a pin list (SET semantics for the grid — the pinned
 * block keeps the grid's own column order). Returns a NEW array. */
export function togglePin(pins: string[], colId: string): string[] {
  return pins.includes(colId) ? pins.filter((c) => c !== colId) : [...pins, colId];
}

// ---------------------------------------------------------------------
// Generic display text from the defs themselves — the fallback lane that
// makes future columns participate automatically. Callers layer their own
// eye-matching overrides on top (home.ts gridDisplayValue).
// ---------------------------------------------------------------------

export function displayValueFromDefs<T>(
  defs: ReadonlyArray<ColDef<T>>,
  colId: string,
  row: T,
): string {
  const def = defs.find((d) => d.colId === colId);
  if (!def) return '';
  let v: unknown = '';
  if (typeof def.valueGetter === 'function') {
    // The home grid's valueGetters read only p.data (measured across the
    // catalog); the cast hands them exactly that.
    v = (def.valueGetter as unknown as (p: { data: T }) => unknown)({ data: row });
  } else if (def.field) {
    v = (row as Record<string, unknown>)[def.field as string];
  }
  const s = v === null || v === undefined ? '' : String(v);
  // The grid's em-dash placeholder IS blank to the filter/copy vocabulary.
  return s === '—' ? '' : s;
}

/** One column's copy text: one line per row, blanks kept as empty lines so
 * row correspondence survives a paste next to another column. */
export function columnCopyText(displays: string[]): string {
  return displays.join('\n');
}

// ---------------------------------------------------------------------
// The sheets-style filter popover — Community IFilterComp, REAL
// doesFilterPass (see the header). AG contributes the header button,
// popup positioning/dismiss and the funnel state via isFilterActive().
// ---------------------------------------------------------------------

export interface HomeFilterHost {
  colId: string;
  getState(): ColumnFilterState | undefined;
  /** null clears the column's filter entirely. */
  setState(state: ColumnFilterState | null): void;
  /** Distinct display values over the CURRENT candidate rows. */
  valuesFor(): { value: string; count: number }[];
  /** The display string for one row's cell in this column. */
  displayFor(row: unknown): string;
  /** MULTI-VALUE lane (see the header): the per-entity display strings for
   * one row's cell, or null for a single-valued column. Non-null routes
   * doesFilterPass through multiDisplayPassesFilter. */
  displaysFor?(row: unknown): string[] | null;
  /** Called after a USER write (not model seeding) — persist lives there. */
  onWrite(): void;
}

const TEXT_OPS: [FilterOp | '', string][] = [
  ['', 'No condition'],
  ['contains', 'Contains'],
  ['not-contains', 'Does not contain'],
  ['equals', 'Equals'],
  ['not-equals', 'Does not equal'],
  ['starts', 'Starts with'],
  ['ends', 'Ends with'],
  ['gt', '>'],
  ['gte', '≥'],
  ['lt', '<'],
  ['lte', '≤'],
  ['blank', 'Is blank'],
  ['not-blank', 'Is not blank'],
];

/** Render cap for the value list — search narrows past it. */
const LIST_CAP = 300;

export class HomeColumnFilter implements IFilterComp {
  private gui!: HTMLElement;
  private host!: HomeFilterHost;
  private params!: IFilterParams & { host: HomeFilterHost };
  private search = '';

  init(params: IFilterParams & { host: HomeFilterHost }): void {
    this.params = params;
    this.host = params.host;
    this.gui = document.createElement('div');
    this.gui.className = 'hc-flt';
    this.render();
  }

  getGui(): HTMLElement {
    return this.gui;
  }

  /** REAL — AG's own pipeline drops the rows (no pure projection layer on
   * this surface; the Glance twin's vacuous-true rationale does not hold).
   * A multi-valued column (host.displaysFor non-null) is judged entity by
   * entity, never on the joined display string. */
  doesFilterPass(p: { data: unknown }): boolean {
    const many = this.host.displaysFor?.(p.data) ?? null;
    if (many !== null) return multiDisplayPassesFilter(many, this.host.getState());
    return displayPassesFilter(this.host.displayFor(p.data), this.host.getState());
  }

  isFilterActive(): boolean {
    return columnFilterActive(this.host.getState());
  }

  getModel(): ColumnFilterState | null {
    const s = this.host.getState();
    return s && columnFilterActive(s) ? { ...s } : null;
  }

  /** Model seeding (restore path) — writes state WITHOUT onWrite: seeding a
   * restored filter back through persist would re-save what was just
   * loaded. */
  setModel(model: ColumnFilterState | null): void {
    this.host.setState(model && columnFilterActive(model) ? { ...model } : null);
    if (this.gui) this.render();
  }

  afterGuiAttached(): void {
    this.render(); // values re-collect on every open — rows change under it
    this.gui.querySelector<HTMLInputElement>('.hc-flt-search')?.focus();
  }

  // -------------------------------------------------------------------

  private write(patch: Partial<ColumnFilterState>): void {
    const cur = this.host.getState() ?? {};
    const next: ColumnFilterState = { ...cur, ...patch };
    // Normalize to "no filter" when nothing constrains — the funnel must
    // never claim an active filter that keeps everything.
    const nothing =
      (next.selected === null || next.selected === undefined) &&
      (!next.op || (next.op !== 'blank' && next.op !== 'not-blank' && !String(next.operand ?? '').trim()));
    this.host.setState(nothing ? null : next);
    this.params.filterChangedCallback();
    this.host.onWrite();
    this.render();
  }

  private render(): void {
    const state = this.host.getState() ?? {};
    const values = this.host.valuesFor();
    const total = values.length;
    const selected = state.selected ?? null;
    const selCount = selected === null ? total : selected.length;
    const needle = this.search.trim().toLowerCase();
    const listed = needle ? values.filter((v) => v.value.toLowerCase().includes(needle)) : values;
    const shown = listed.slice(0, LIST_CAP);

    this.gui.replaceChildren();

    // ---- criteria ----
    const crit = document.createElement('div');
    crit.className = 'hc-flt-crit';
    const opSel = mkFcSelect({
      cls: 'hc-flt-op',
      label: 'Filter operator',
      options: TEXT_OPS.map(([op, label]) => ({ value: op, label })),
      value: state.op ?? '',
      onPick: (v) => {
        const op = (v || null) as FilterOp | null;
        this.write({ op, ...(op === 'blank' || op === 'not-blank' ? { operand: '' } : {}) });
      },
    });
    const operand = document.createElement('input');
    operand.className = 'hc-flt-operand';
    operand.type = 'text';
    operand.placeholder = 'value…';
    operand.value = state.operand ?? '';
    operand.disabled = !state.op || state.op === 'blank' || state.op === 'not-blank';
    let operandTimer: ReturnType<typeof setTimeout> | null = null;
    operand.addEventListener('input', () => {
      if (operandTimer) clearTimeout(operandTimer);
      const v = operand.value;
      operandTimer = setTimeout(() => this.write({ operand: v }), 250);
    });
    crit.append(opSel.el, operand);
    this.gui.appendChild(crit);

    // ---- search ----
    const search = document.createElement('input');
    search.className = 'hc-flt-search';
    search.type = 'text';
    search.placeholder = 'Search values…';
    search.value = this.search;
    search.addEventListener('input', () => {
      this.search = search.value;
      this.renderPreservingFocus(search);
    });
    this.gui.appendChild(search);

    // ---- select all / clear + the measured count ----
    const bar = document.createElement('div');
    bar.className = 'hc-flt-bar';
    // Both bulk-selection controls WRITE the filter model, so they are
    // action buttons and ride the primitive icon-only (the word each used
    // to show is now the whole tooltip/aria name). The measured "X of Y"
    // count beside them is unchanged — it is the label that answers "what
    // did that just do".
    const all = mkIconBtn({ glyph: 'circle-check', label: 'Select all values', size: 'sm' });
    all.addEventListener('click', () => this.write({ selected: null }));
    const none = mkIconBtn({ glyph: 'circle-x', label: 'Clear the selected values', size: 'sm' });
    none.addEventListener('click', () => this.write({ selected: [] }));
    const count = document.createElement('span');
    count.className = 'hc-flt-count';
    count.textContent = `${selCount} of ${total}`;
    bar.append(all, none, count);
    this.gui.appendChild(bar);

    // ---- the value list ----
    const list = document.createElement('div');
    list.className = 'hc-flt-list';
    const selectedSet = selected === null ? null : new Set(selected);
    for (const v of shown) {
      const row = document.createElement('div');
      row.className = 'hc-flt-row';
      // A filter-VALUE row: it decides what the grid SHOWS, not which rows
      // an action applies to, so the ruling's grid-bulk-select carve-out
      // does not reach it — it is a toggle.
      const tg = mkFcToggle({
        label: v.value === BLANK_VALUE ? '(blanks)' : v.value,
        text: '',
        checked: selectedSet === null || selectedSet.has(v.value),
        onChange: (on) => this.toggleValue(v.value, on, values),
      });
      const t = document.createElement('span');
      t.className = 'hc-flt-val';
      t.textContent = v.value;
      if (v.value === BLANK_VALUE) t.classList.add('blank');
      const n = document.createElement('span');
      n.className = 'hc-flt-n';
      n.textContent = String(v.count);
      row.append(tg.el, t, n);
      list.appendChild(row);
    }
    if (listed.length > LIST_CAP) {
      const more = document.createElement('div');
      more.className = 'hc-flt-more';
      more.textContent = `${listed.length - LIST_CAP} more — search to narrow`;
      list.appendChild(more);
    }
    if (listed.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'hc-flt-more';
      empty.textContent = 'No values match the search.';
      list.appendChild(empty);
    }
    this.gui.appendChild(list);
  }

  /** Re-render but keep the search box focused with its caret. */
  private renderPreservingFocus(search: HTMLInputElement): void {
    const pos = search.selectionStart ?? search.value.length;
    this.render();
    const next = this.gui.querySelector<HTMLInputElement>('.hc-flt-search');
    if (next) {
      next.focus();
      next.setSelectionRange(pos, pos);
    }
  }

  private toggleValue(value: string, nowChecked: boolean, values: { value: string }[]): void {
    const state = this.host.getState() ?? {};
    const all = values.map((v) => v.value);
    const cur = new Set(state.selected ?? all);
    if (nowChecked) cur.add(value);
    else cur.delete(value);
    // Everything checked normalizes back to "no value constraint".
    const next = cur.size >= all.length && all.every((v) => cur.has(v)) ? null : [...cur];
    this.write({ selected: next });
  }
}

// ---------------------------------------------------------------------
// The one decoration pass — every def in, every def out with the filter
// and the shipped default pins attached. Applied by buildGrid over
// WHATEVER defs exist, which is what makes new columns participate
// automatically.
// ---------------------------------------------------------------------

export interface GridMachineryOpts {
  /** Shipped default pins (colIds), the seat's `pins` field overriding. */
  defaultPins: readonly string[];
  /** colIds that take NO filter (the actions cell has no values). */
  unfiltered?: readonly string[];
  getFilter(colId: string): ColumnFilterState | undefined;
  setFilter(colId: string, s: ColumnFilterState | null): void;
  /** Display text for a cell (the eye's vocabulary; '' = blank). */
  displayValue(colId: string, row: unknown): string;
  /** MULTI-VALUE lane: per-entity display strings for a cell that holds a
   * LIST (the Active-users column offers each user's NAME as its own
   * filter value). Return null for single-valued columns — the default
   * lane. An empty array is a BLANK cell. */
  displayValues?(colId: string, row: unknown): string[] | null;
  /** The rows the values list counts over. */
  rows(): unknown[];
  /** Persist hook, called after any USER filter write. */
  onFilterWrite(): void;
}

export function withGridMachinery<T>(defs: ColDef<T>[], opts: GridMachineryOpts): ColDef<T>[] {
  const skip = new Set(opts.unfiltered ?? []);
  return defs.map((def) => {
    const colId = def.colId;
    if (!colId) return def;
    const out: ColDef<T> = { ...def };
    if (opts.defaultPins.includes(colId)) out.pinned = 'left';
    if (!skip.has(colId)) {
      const host: HomeFilterHost = {
        colId,
        getState: () => opts.getFilter(colId),
        setState: (s) => opts.setFilter(colId, s),
        // Multi-valued cells FLATTEN into the value list (each entity its
        // own row; an entity-less cell counts once as (Blanks)); single-
        // valued columns ride the one-display lane unchanged.
        valuesFor: () =>
          collectValues(opts.rows().flatMap((r) => {
            const many = opts.displayValues?.(colId, r) ?? null;
            if (many === null) return [opts.displayValue(colId, r)];
            return many.length ? many : [''];
          })),
        displayFor: (row) => opts.displayValue(colId, row),
        displaysFor: (row) => opts.displayValues?.(colId, row) ?? null,
        onWrite: () => opts.onFilterWrite(),
      };
      out.filter = HomeColumnFilter;
      out.filterParams = { host };
    }
    return out;
  });
}

// ---------------------------------------------------------------------
// Seat blob (ui_state grid-preset 'home'). Legacy seats are a BARE ARRAY of
// AG column state; the machinery blob wraps it. parse accepts both so no
// user's saved layout is dropped on rollout.
// ---------------------------------------------------------------------

export interface HomeGridSeatBlob {
  columns?: unknown[] | null;
  /** Pinned-left colIds. ABSENT = shipped defaults; [] = deliberately
   * unpinned (the two must stay distinguishable — see effectivePins). */
  pins?: string[] | null;
  colFilters?: ColumnFilterMap;
}

export function parseGridSeatBlob(state: unknown): HomeGridSeatBlob {
  if (Array.isArray(state)) return { columns: state }; // legacy shape
  if (state && typeof state === 'object') {
    const o = state as HomeGridSeatBlob;
    return {
      columns: Array.isArray(o.columns) ? o.columns : null,
      pins: Array.isArray(o.pins) ? o.pins.map(String) : null,
      colFilters:
        o.colFilters && typeof o.colFilters === 'object' && !Array.isArray(o.colFilters)
          ? (o.colFilters as ColumnFilterMap)
          : undefined,
    };
  }
  return {};
}
